// Zambia — Chisolo (Natatu) engine.
//
// Board: 4 rows × 7 pits.
//   row 0 = B outer (top), row 1 = B inner,
//   row 2 = A inner, row 3 = A outer (bottom).
//
// Opening: outer rows have 3 seeds per pit; inner rows have, from each
// player's own perspective left-to-right: [2, 1, 0, 0, 3, 3, 3].
//   Player A's inner row (row 2) maps directly: col 0→2, 1→1, 2→0, 3→0,
//     4→3, 5→3, 6→3.
//   Player B's inner row (row 1) is mirrored (their left is the board's right):
//     col 6→2, 5→1, 4→0, 3→0, 2→3, 1→3, 0→3.
//
// Direction:
//   - Player A's first move is forced clockwise (CW).
//   - Player B's first move is chosen by Player B and locks for the rest
//     of the game. For v1, the engine accepts either CW or CCW for B and
//     locks on first move; if Player B does not explicitly choose,
//     anti-clockwise (CCW) is used.
//
// Sowing: relay-sowing around the active player's own two rows only,
// continuing until the final counter lands in a previously-empty pit.
// Singleton restriction (Tsoro family): can't start from a 1-seed pit
// while any 2+ pit exists.
//
// Capture: empty inner-row landing AND opposite enemy inner pit has counters
//   → take the opposite inner pit, plus the paired enemy outer pit (if any),
//   plus ONE free additional opponent pit. The free pit is auto-selected as
//   the opponent's largest remaining pit. (Player picker may replace this
//   auto-selection in a future iteration.)
//
// End: opponent has zero counters on board → active player wins.
//      Opponent has no legal move → active player wins.

export const RULES_VERSION = "2026.02.18";
export const RULESET_ID = "zm-chisolo-natatu";
export const ROWS = 4;
export const COLS = 7;
export const PLAYER_A = "A";
export const PLAYER_B = "B";

// CCW routes: outer row left→right then inner row right→left (each player's
// own loop).
const ROUTE_A_CCW = [
  [3, 0], [3, 1], [3, 2], [3, 3], [3, 4], [3, 5], [3, 6],
  [2, 6], [2, 5], [2, 4], [2, 3], [2, 2], [2, 1], [2, 0],
];
const ROUTE_B_CCW = [
  [0, 6], [0, 5], [0, 4], [0, 3], [0, 2], [0, 1], [0, 0],
  [1, 0], [1, 1], [1, 2], [1, 3], [1, 4], [1, 5], [1, 6],
];
const ROUTE_A_CW = [...ROUTE_A_CCW].reverse();
const ROUTE_B_CW = [...ROUTE_B_CCW].reverse();

function routeFor(p, dir) {
  if (dir === "CW") return p === PLAYER_A ? ROUTE_A_CW : ROUTE_B_CW;
  return p === PLAYER_A ? ROUTE_A_CCW : ROUTE_B_CCW;
}
function ownsRow(p, row) { return p === PLAYER_A ? row === 2 || row === 3 : row === 0 || row === 1; }
function innerRow(p) { return p === PLAYER_A ? 2 : 1; }
function oppInner(p) { return p === PLAYER_A ? 1 : 2; }
function oppOuter(p) { return p === PLAYER_A ? 0 : 3; }
function emptyBoard() { return Array.from({ length: ROWS }, () => Array(COLS).fill(0)); }
function cloneBoard(b) { return b.map((r) => r.slice()); }

export function createInitialState() {
  const board = emptyBoard();
  // Outer rows: 3 per pit.
  for (let c = 0; c < COLS; c++) {
    board[0][c] = 3;
    board[3][c] = 3;
  }
  // Inner rows (from each player's perspective: [2,1,0,0,3,3,3]).
  const innerA = [2, 1, 0, 0, 3, 3, 3];
  for (let c = 0; c < COLS; c++) {
    board[2][c] = innerA[c];
    board[1][COLS - 1 - c] = innerA[c];
  }
  return {
    rulesetId: RULESET_ID,
    rulesVersion: RULES_VERSION,
    board,
    currentPlayer: PLAYER_A,
    phase: "NORMAL",
    captures: { A: 0, B: 0 },
    status: "ACTIVE",
    winner: null,
    endReason: null,
    turnNumber: 1,
    moveNumber: 0,
    history: [],
    directions: { A: "CW", B: null }, // A is forced CW; B locks on first move
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

function ownPits(b, p) {
  const rows = p === PLAYER_A ? [2, 3] : [0, 1];
  const out = [];
  for (const r of rows) for (let c = 0; c < COLS; c++) if (b[r][c] > 0) out.push([r, c]);
  return out;
}
function countersOf(b, p) {
  const rows = p === PLAYER_A ? [2, 3] : [0, 1];
  let s = 0; for (const r of rows) for (let c = 0; c < COLS; c++) s += b[r][c]; return s;
}
function getPhase(b, p) {
  const own = ownPits(b, p);
  if (own.length === 0) return "NORMAL";
  return own.some(([r, c]) => b[r][c] >= 2) ? "NORMAL" : "SINGLETON";
}
function routeIndex(p, dir, r, c) {
  const route = routeFor(p, dir);
  for (let i = 0; i < route.length; i++) if (route[i][0] === r && route[i][1] === c) return i;
  return -1;
}
function nextPit(p, dir, r, c) {
  const route = routeFor(p, dir);
  const i = routeIndex(p, dir, r, c);
  return route[(i + 1) % route.length];
}

function dirFor(state, player) {
  return state.directions?.[player] || (player === PLAYER_A ? "CW" : "CCW");
}

export function getLegalMoves(state) {
  const { board, currentPlayer } = state;
  const dir = dirFor(state, currentPlayer);
  const phase = getPhase(board, currentPlayer);
  const own = ownPits(board, currentPlayer);
  const legal = [];
  if (phase === "SINGLETON") {
    for (const [r, c] of own) {
      if (board[r][c] !== 1) continue;
      const [nr, nc] = nextPit(currentPlayer, dir, r, c);
      if (board[nr][nc] === 0) legal.push([r, c]);
    }
  } else {
    for (const [r, c] of own) if (board[r][c] >= 2) legal.push([r, c]);
  }
  return legal;
}

export function isLegalMove(state, r, c) {
  return getLegalMoves(state).some(([rr, cc]) => rr === r && cc === c);
}

function sowAndRelay(board, player, dir, startR, startC) {
  const route = routeFor(player, dir);
  let b = cloneBoard(board);
  let inHand = b[startR][startC];
  b[startR][startC] = 0;
  let idx = routeIndex(player, dir, startR, startC);
  let last = [startR, startC];
  let relays = 0;
  let safety = 0;
  while (true) {
    while (inHand > 0) {
      idx = (idx + 1) % route.length;
      const [nr, nc] = route[idx];
      b[nr][nc] += 1;
      last = [nr, nc];
      inHand -= 1;
      if (++safety > 5000) throw new Error("SAFETY_BREAK");
    }
    if (b[last[0]][last[1]] === 1) break;
    inHand = b[last[0]][last[1]];
    b[last[0]][last[1]] = 0;
    relays += 1;
    idx = routeIndex(player, dir, last[0], last[1]);
  }
  return { board: b, landing: last, relays };
}

export function applyMove(state, r, c) {
  if (state.status !== "ACTIVE") throw new Error("GAME_NOT_ACTIVE");
  if (!ownsRow(state.currentPlayer, r)) throw new Error("NOT_OWN_PIT");
  if (!isLegalMove(state, r, c)) throw new Error("ILLEGAL_MOVE");

  const player = state.currentPlayer;
  const opp = player === PLAYER_A ? PLAYER_B : PLAYER_A;
  const dir = dirFor(state, player);

  const events = [];
  events.push({ type: "TURN_STARTED", player, turn: state.turnNumber });
  events.push({ type: "PIT_SELECTED", from: [r, c], count: state.board[r][c] });

  const { board: postBoard, landing, relays } = sowAndRelay(state.board, player, dir, r, c);
  events.push({ type: "SOWING_COMPLETE", landing, relays });

  let board = postBoard;
  let captured = 0;
  const capturedPits = [];

  if (landing[0] === innerRow(player)) {
    const col = landing[1];
    const oi = oppInner(player);
    const oo = oppOuter(player);
    if (board[oi][col] > 0) {
      board = cloneBoard(board);
      // Primary capture: opposite inner.
      const n1 = board[oi][col]; board[oi][col] = 0; captured += n1; capturedPits.push([oi, col]);
      events.push({ type: "CAPTURE_TRIGGERED", at: landing });
      events.push({ type: "COUNTERS_CAPTURED", from: [oi, col], count: n1 });
      // Paired outer.
      if (board[oo][col] > 0) {
        const n2 = board[oo][col]; board[oo][col] = 0; captured += n2; capturedPits.push([oo, col]);
        events.push({ type: "COUNTERS_CAPTURED", from: [oo, col], count: n2 });
      }
      // 1 free pit: auto-pick the opponent's largest remaining pit.
      const oppRows = opp === PLAYER_A ? [2, 3] : [0, 1];
      let bestR = -1, bestC = -1, bestN = 0;
      for (const rr of oppRows) for (let cc = 0; cc < COLS; cc++) {
        if (board[rr][cc] > bestN) { bestN = board[rr][cc]; bestR = rr; bestC = cc; }
      }
      if (bestN > 0) {
        board[bestR][bestC] = 0; captured += bestN; capturedPits.push([bestR, bestC]);
        events.push({ type: "FREE_CAPTURE", from: [bestR, bestC], count: bestN });
      }
    }
  }

  const captures = { A: state.captures.A, B: state.captures.B };
  captures[player] += captured;

  // Lock Player B's direction after their first move.
  const newDirections = { ...(state.directions || { A: "CW", B: null }) };
  if (player === PLAYER_B && newDirections.B == null) newDirections.B = dir;

  let status = "ACTIVE", winner = null, endReason = null;
  if (countersOf(board, opp) === 0) {
    status = "WON"; winner = player; endReason = "OPPONENT_CLEARED";
    events.push({ type: "GAME_WON", player, reason: endReason });
  }
  if (status === "ACTIVE") {
    const probe = { ...state, board, currentPlayer: opp, captures, directions: newDirections };
    if (getLegalMoves(probe).length === 0) {
      status = "WON"; winner = player; endReason = "OPPONENT_NO_LEGAL_MOVE";
      events.push({ type: "GAME_WON", player, reason: endReason });
    }
  }

  events.push({ type: "TURN_ENDED", player, landingPit: landing, captured, relays });

  const historyEntry = {
    player, from: [r, c], landingPit: landing, captured, capturedPits, relays,
    prevBoard: state.board, prevCaptures: state.captures, prevPhase: state.phase,
    prevCurrentPlayer: state.currentPlayer, prevStatus: state.status, prevWinner: state.winner,
    prevEndReason: state.endReason, prevTurnNumber: state.turnNumber, prevDirections: state.directions,
    events,
  };

  return {
    state: {
      ...state,
      board,
      currentPlayer: status === "ACTIVE" ? opp : state.currentPlayer,
      captures, status, winner, endReason, directions: newDirections,
      turnNumber: status === "ACTIVE" ? state.turnNumber + 1 : state.turnNumber,
      moveNumber: state.moveNumber + 1,
      history: [...state.history, historyEntry],
      updatedAt: Date.now(),
    },
    events,
  };
}

export function undoMove(state) {
  if (!state.history.length) return state;
  const last = state.history[state.history.length - 1];
  return {
    ...state,
    board: last.prevBoard,
    currentPlayer: last.prevCurrentPlayer,
    phase: last.prevPhase,
    captures: last.prevCaptures,
    status: last.prevStatus,
    winner: last.prevWinner,
    endReason: last.prevEndReason,
    turnNumber: last.prevTurnNumber,
    directions: last.prevDirections,
    moveNumber: Math.max(0, state.moveNumber - 1),
    history: state.history.slice(0, -1),
    updatedAt: Date.now(),
  };
}

export function totalCounters(state) {
  let s = state.captures.A + state.captures.B;
  for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) s += state.board[r][c];
  return s;
}

export function describeMove(entry) {
  if (!entry) return "";
  const p = entry.player === PLAYER_A ? "Player 1" : "Player 2";
  const parts = [`${p} sowed around their own two rows.`];
  if (entry.relays > 0) parts.push(`Relayed ${entry.relays} time${entry.relays === 1 ? "" : "s"}.`);
  if (entry.captured > 0) {
    parts.push(`Captured ${entry.captured} seeds (opposite inner + paired outer + free pit).`);
  } else {
    parts.push("No capture.");
  }
  return parts.join(" ");
}
